/**
 * The one way to mark the media editor as having unsaved edits (VE-27765).
 *
 * `tdoIsChanged` alone cannot tell a save apart from an edit that landed while
 * that save was uploading: the save clears the flag when it lands, wiping the
 * mid-save edit's `true` with it. `saveRev` is a monotonic counter bumped on
 * every edit; a save carries the rev it snapshotted, and `SAVE_MEDIA_DATA_SUCCESS`
 * only clears the flag when the rev hasn't moved since.
 *
 * Every dirtying reducer must go through these helpers rather than setting
 * `tdoIsChanged` directly — a raw write skips the rev bump and reopens the
 * race. An ESLint rule (`no-restricted-syntax`, eslint.config.mjs) enforces
 * this across the mediaDetails module.
 *
 * `saveRev` must never move backwards, so undo/redo strip it from the patches
 * they replay (see `withoutSaveRev`) and the route resets carry it over.
 *
 * Reading the rev on the main thread before the worker snapshots is safe
 * because every edit dispatched before IN_SAVE_CHANGES crosses to the worker
 * ahead of it, through the same FIFO action buffer
 * (utils/workeredux/actionBuffer.ts): the snapshot can only be NEWER than the
 * rev, which costs a redundant save, never a lost edit. An edit made inside the
 * worker bumps the rev only when its OUT_* action reaches the main thread, by
 * which time the worker already holds it — the same safe direction.
 */
import type { Draft, Patch } from 'immer';
import type { MainStore } from '../store';

type SaveRevState = Pick<MainStore, 'tdoIsChanged' | 'saveRev'>;

/** Mark dirty inside an immer draft. */
export const markTdoChanged = (draft: Draft<SaveRevState>) => {
  draft.tdoIsChanged = true;
  draft.saveRev += 1;
};

/** Mark dirty in a spread-style reducer: `{ ...state, ...tdoChangedFields(state) }`. */
export const tdoChangedFields = (state: SaveRevState) => ({
  tdoIsChanged: true,
  saveRev: state.saveRev + 1,
});

/** Drop `saveRev` from recorded patches so undo/redo can't rewind the counter. */
export const withoutSaveRev = (patches: readonly Patch[]) =>
  patches.filter((patch) => patch.path[0] !== 'saveRev');
